import { decodeMlsMessage, encodeMlsMessage, } from 'ts-mls';
import { CIPHER_SUITE, ENVELOPE_VERSION, decodeEnvelope, encodeEnvelope, EnvelopeError, } from '@fh/shared';
import { b64 } from './suite.js';
import { epoche } from './group.js';
/**
 * Das Bindeglied zwischen MLS und dem Envelope-Format (SECURITY.md §3.4).
 *
 * Der Envelope ist das, was in `message.envelope` landet. Der Server liest daraus
 * ausschließlich Version, Suite, Epoche und Sequenz — genau die Felder, die er
 * für Reihenfolge und Replay-Schutz braucht. Der Payload ist für ihn eine
 * Bytefolge.
 *
 * Der Wechsel von `v0-plain` auf `v1-mls` passiert hier und nur hier. Das
 * Datenbankschema ändert sich dabei nicht — das war der Grund, das Format vor
 * der ersten Zeile Chat-Code festzulegen.
 */
/**
 * Laufende Nummer je (Gerät, Epoche).
 *
 * Der Server hat einen Unique-Index über `(room_id, sender_device, mls_epoch,
 * mls_seq)` — das ist der Replay-Schutz. Zwei Nachrichten mit derselben Nummer
 * werden abgelehnt, also muss der Zähler bei jeder Nachricht hochlaufen und
 * bei jedem Epochenwechsel neu beginnen.
 */
export class SequenzZaehler {
    epoche = null;
    naechste = 0n;
    next(aktuelleEpoche) {
        if (this.epoche !== aktuelleEpoche) {
            this.epoche = aktuelleEpoche;
            this.naechste = 0n;
        }
        return this.naechste++;
    }
}
/** MLS-Nachricht → Envelope (Base64), so wie er zum Server geht. */
export function alsEnvelope(nachricht, gruppe, seq) {
    const bytes = encodeEnvelope({
        version: ENVELOPE_VERSION.MLS,
        suite: CIPHER_SUITE.MLS_1,
        header: {
            groupId: gruppe.state.groupContext.groupId,
            epoch: epoche(gruppe),
            senderIndex: 0,
            seq,
        },
        payload: encodeMlsMessage(nachricht),
    });
    return b64.encode(bytes);
}
/** Envelope (Base64) → MLS-Nachricht. Wirft bei fremdem Format. */
export function ausEnvelope(envelopeB64) {
    const env = decodeEnvelope(b64.decode(envelopeB64));
    if (env.version === ENVELOPE_VERSION.PLAIN) {
        throw new EnvelopeError('Diese Nachricht stammt aus der Zeit vor der Verschlüsselung (v0-plain)');
    }
    if (env.version !== ENVELOPE_VERSION.MLS) {
        throw new EnvelopeError(`Unbekannte Envelope-Version ${String(env.version)}`);
    }
    const dekodiert = decodeMlsMessage(env.payload, 0);
    if (!dekodiert)
        throw new EnvelopeError('MLS-Nachricht unlesbar');
    return dekodiert[0];
}
/**
 * KeyPackages und Welcomes gehen ebenfalls als MLS-Nachricht über die Leitung.
 * ts-mls exportiert für sie keine eigenen Encoder — der Rahmen ist der
 * vorgesehene Weg und spart zwei Formate.
 */
export function keyPackageAlsBytes(kp) {
    return b64.encode(encodeMlsMessage({
        wireformat: 'mls_key_package', keyPackage: kp, version: 'mls10',
    }));
}
export function keyPackageAusBytes(text) {
    const msg = decodeMlsMessage(b64.decode(text), 0);
    if (!msg || msg[0].wireformat !== 'mls_key_package') {
        throw new EnvelopeError('Kein gültiges KeyPackage');
    }
    return msg[0].keyPackage;
}
export function welcomeAlsBytes(w) {
    return b64.encode(encodeMlsMessage({
        wireformat: 'mls_welcome', welcome: w, version: 'mls10',
    }));
}
export function welcomeAusBytes(text) {
    const msg = decodeMlsMessage(b64.decode(text), 0);
    if (!msg || msg[0].wireformat !== 'mls_welcome') {
        throw new EnvelopeError('Kein gültiges Welcome');
    }
    return msg[0].welcome;
}
export function commitAlsBytes(commit) {
    return b64.encode(encodeMlsMessage(commit));
}
export function commitAusBytes(text) {
    const msg = decodeMlsMessage(b64.decode(text), 0);
    if (!msg)
        throw new EnvelopeError('Commit unlesbar');
    return msg[0];
}
/** Ob eine Nachricht schon verschlüsselt ist — für die Anzeige in der UI. */
export function istVerschluesselt(envelopeB64) {
    try {
        return decodeEnvelope(b64.decode(envelopeB64)).version === ENVELOPE_VERSION.MLS;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=wire.js.map